import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, delay } from 'rxjs';
import ApiOptionsConfig from './api-options.config';
import { CustomerService } from './customer.service';
import { JurisdictionsService } from './general-settings/jurisdictions.service';
import { ItemProductsService } from './general-settings/item-products.service';
import { ItemTypesService } from './general-settings/item-types.service';
import { LawsService } from './general-settings/laws.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { StoreService } from '../store.service';

const BASE_URL = '/proposals';
const BASE_URL_STEP = '/proposal-steps';
const BASE_URL_PAYMENT_TERMS = '/proposal-payment-terms';
const BASE_URL_STATUS = '/proposal-statuses';
const BASE_URL_CHANGE_STATUS = '/change-status';
const BASE_URL_PAYMENT_METHODS = '/proposal-payment-condition-types';
const BASE_URL_ITEMS = '/proposal-items';
const BASE_URL_INIT_FROM = '/init-from-';
const BASE_URL_INIT_CHANGES = '/init-change-of-scope';
const BASE_URL_GO_TO_PROJECT = '/projects/by-proposal';

@Injectable({
  providedIn: 'root'
})
export class ProposalsService {

  accessToken: string = '';

  storeService: StoreService;
  httpClient: HttpClient;


  private customersSubject = new BehaviorSubject<string[]>([]);
  public customers$ = this.customersSubject.asObservable();
  private jurisdictionsSubject = new BehaviorSubject<string[]>([]);
  public jurisdictions$ = this.jurisdictionsSubject.asObservable();

  private itemProductsByJurisdictionCache: { [key: string]: any[] } = {};

  private itemTypesByJurisdictionAndProductCache: { key: string, value: string }[] = [];

  private lawsByJurisdictionCache: { [jurisdictionId: string]: { key: string, value: string }[] } = {};

  constructor(private customerService: CustomerService,
    private jurisdictionsService: JurisdictionsService,
    private itemProductsService: ItemProductsService,
    private itemTypesService: ItemTypesService,
    private lawsService: LawsService) {
    const params = new HttpParams().set('size', '9999');
    this.customerService.list().subscribe({
      next: (response: any) => {
        const updatedCustomers = response.data.customers
        this.customersSubject.next(updatedCustomers);
      },
      error: (error: any) => {
        console.error('Error fetching customers:', error);
      }
    });
    this.jurisdictionsService.list({ params }).subscribe({
      next: (response: any) => {
        const updatedJurisdictions = response?.data?.jurisdictions?.map((jurisdiction: any) => ({
          key: jurisdiction.jurisdictionId,
          value: jurisdiction.name
        }));
        this.jurisdictionsSubject?.next(updatedJurisdictions);
      },
      error: (error: any) => {
        console.error('Error fetching jurisdictions:', error);
      }
    });


    this.storeService = inject(StoreService);
    this.httpClient = inject(HttpClient);

    this.storeService.getAuthToken().subscribe(
      (data: any) => { this.accessToken = data ? data['accessToken'] : null }
    )
  }


  loadLawsByJurisdiction(jurisdictionId?: number | string): Observable<{ key: string, value: string }[]> {
    if (!jurisdictionId) {
      return of([]);
    }

    if (this.lawsByJurisdictionCache[jurisdictionId]) {
      return of(this.lawsByJurisdictionCache[jurisdictionId]);
    }

    const params = new HttpParams()
      .set('size', '9999')
      .set('jurisdictionId', jurisdictionId);

    return new Observable<{ key: string, value: string }[]>(observer => {
      this.lawsService.list({ params }).subscribe({
        next: (response: any) => {
          if (response?.data?.laws) {
            const laws = response.data.laws.map((law: any) => ({
              key: law.lawId,
              value: law.name
            })) || [];

            this.lawsByJurisdictionCache[jurisdictionId] = laws;

            observer.next(laws);
            observer.complete();
          } else {
            this.lawsByJurisdictionCache[jurisdictionId] = [];
            observer.next([]);
            observer.complete();
          }
        },
        error: (error: any) => {
          console.error('Error fetching laws for jurisdiction:', error);
          observer.error(error);
        }
      });
    });
  }


  getItemProductsByJurisdiction(jurisdictionId: string): Observable<string[]> {
    if (this.itemProductsByJurisdictionCache[jurisdictionId]) {
      return of(this.itemProductsByJurisdictionCache[jurisdictionId]).pipe(delay(100));
    }
    const params = new HttpParams()
      .set('size', '9999')
      .set('jurisdictionId', jurisdictionId);

    return new Observable<string[]>(observer => {
      this.itemProductsService.list({ params }).subscribe({
        next: (response: any) => {
          const filteredProducts = response?.data?.itemProducts?.map((itemsProduct: any) => ({
            key: itemsProduct.itemProductId,
            value: itemsProduct.name
          })) || [];
          this.itemProductsByJurisdictionCache[jurisdictionId] = filteredProducts;
          observer.next(filteredProducts);
          observer.complete();
        },
        error: (error: any) => {
          console.error('Error fetching products for jurisdiction:', error);
          observer.error(error);
        }
      });
    });
  }

  getItemTypesByJurisdictionAndProduct(jurisdictionId: string, itemProductId: string): Observable<{ key: string; value: string; }> {
    const cacheKey: any = `${jurisdictionId}_${itemProductId}`;
    if (this.itemTypesByJurisdictionAndProductCache[cacheKey]) {
      return of(this.itemTypesByJurisdictionAndProductCache[cacheKey]).pipe(delay(100));
    }

    const params = new HttpParams()
      .set('size', '9999')
      .set('jurisdictionId', jurisdictionId)
      .set('itemProductId', itemProductId);

    return new Observable<{ key: string; value: string; }>(observer => {
      this.itemTypesService.list({ params }).subscribe({
        next: (response: any) => {
          const filteredTypes = response?.data?.itemTypes?.map((itemType: any) => ({
            key: itemType.itemTypeId,
            value: itemType.name
          })) || [];
          this.itemTypesByJurisdictionAndProductCache[cacheKey] = filteredTypes;
          observer.next(filteredTypes);
          observer.complete();
        },
        error: (error: any) => {
          console.error('Error fetching types for jurisdiction and product:', error);
          observer.error(error);
        }
      });
    });
  }



  list(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL), options?.params);
  }

  detail(proposalId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${proposalId}`), options?.params);
  }

  create(data: any): Observable<any> {
    return this.POST(this.getUrl(BASE_URL), data);
  }

  update(proposalId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${proposalId}`), data);
  }

  delete(proposalId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${proposalId}`));
  }

  changeStatus(proposalId: number, status: string, data?: any): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_CHANGE_STATUS}/${status}`), data || {});
  }

  exportExcel(params: HttpParams): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/excel`), params);
  }

  initFromRequest(requestId: number): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}${BASE_URL_INIT_FROM}request/${requestId}`), {});
  }

  initFromProposal(proposalId: number): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}${BASE_URL_INIT_CHANGES}/${proposalId}`), {});
  }

  listProposalChildren(proposalId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${proposalId}/children`), options?.params);
  }

  listItems(proposalId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_ITEMS}`), options?.params);
  }

  detailItems(proposalId: number, proposalItemId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_ITEMS}/${proposalItemId}`), options?.params);
  }

  createItems(proposalId: number, data: any,): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_ITEMS}`), data);
  }

  updateItems(proposalId: number, proposalItemId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_ITEMS}/${proposalItemId}`), data);
  }

  deleteItems(proposalId: number, proposalItemId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${proposalId}${BASE_URL_ITEMS}/${proposalItemId} `));
  }


  getCustomers(): any[] {
    return this.customersSubject.getValue();
  }

  GET(url: string, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.get<any>(url, {
      headers,
      params
    });
  }

  POST(url: string, data: any, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.post<any>(url, data, {
      headers,
      params
    });
  }


  PUT(url: string, data: any, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.put<any>(url, data, {
      headers,
      params
    });
  }

  DELETE(url: string, params?: HttpParams): Observable<any> {
    let headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    }
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return this.httpClient.delete<any>(url, {
      headers,
      params
    });
  }
  getUrl(path: string): string {
    return `${environment.apiBaseUrl}${path}`
  }

  listPaymentConditionTypes(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_PAYMENT_METHODS), options?.params);
  }

  detailPaymentConditionTypes(proposalPaymentConditionTypeId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_STEP}/${proposalPaymentConditionTypeId}`), options?.params);
  }

  listSettingsProposalSteps(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_STEP), options?.params);
  }

  detailSettingsProposalSteps(proposalStepId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_STEP}/${proposalStepId}`), options?.params);
  }

  createSettingsProposalSteps(data: any): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_STEP), data);
  }

  updateSettingsProposalSteps(proposalStepId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_STEP}/${proposalStepId}`), data);
  }

  deleteSettingsProposalSteps(proposalStepId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_STEP}/${proposalStepId}`));
  }

  listSettingsPaymentTerms(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_PAYMENT_METHODS), options?.params);
  }

  detailSettingsPaymentTerms(proposalPaymentConditionTypeId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_PAYMENT_METHODS}/${proposalPaymentConditionTypeId}`), options?.params);
  }

  createSettingsPaymentTerms(data: any): Observable<any> {
     return this.POST(this.getUrl(BASE_URL_PAYMENT_METHODS), data);
  }

  updateSettingsPaymentTerms(proposalPaymentConditionTypeId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_PAYMENT_METHODS}/${proposalPaymentConditionTypeId}`), data);
  }

  deleteSettingsPaymentTerms(proposalPaymentConditionTypeId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_PAYMENT_METHODS}/${proposalPaymentConditionTypeId}`));
  }



  listProposalSteps(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_STATUS), options?.params);
  }
  detailProposalSteps(proposalStepId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_STATUS}/${proposalStepId}`), options?.params);
  }
  createProposalSteps(data: any): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_STATUS), data);
  }
  updateProposalSteps(proposalStepId: number, data: any): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_STATUS}/${proposalStepId}`), data);
  }
  deleteProposalSteps(proposalStepId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_STATUS}/${proposalStepId}`));
  }


  listNotes(proposalId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${proposalId}/notes`), options?.params);
  }

  createNotes(proposalId: number, data: any): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/${proposalId}/notes`), data);
  }

  deleteNotes(proposalId: number, sectionNoteId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${proposalId}/notes/${sectionNoteId}`));
  }

  createProjectFromProposal(proposalId: number, data: any): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_GO_TO_PROJECT}/${proposalId}`), data);
  }

  getCurrencies(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl('/currencies'), options?.params);
  }


  /*******************************
    START MANAGE SAVE BUTTONS WHEN CREATING A CHANGE OF SCOPE
  *******************************/
 
  private saveButtonDisabledSubject = new BehaviorSubject<boolean>(false);
  public saveButtonDisabled$: Observable<boolean> = this.saveButtonDisabledSubject.asObservable();

  private isFromProposalInitSubject = new BehaviorSubject<boolean>(false);
  public isFromProposalInit$: Observable<boolean> = this.isFromProposalInitSubject.asObservable();

  setSaveButtonDisabled(disabled: boolean): void {
    this.saveButtonDisabledSubject.next(disabled);
  }

  getSaveButtonDisabled(): boolean {
    return this.saveButtonDisabledSubject.value;
  }

  setIsFromProposalInit(value: boolean): void {
    this.isFromProposalInitSubject.next(value);

    if (value) {
      this.setSaveButtonDisabled(true);
    }
  }
  getIsFromProposalInit(): boolean {
    return this.isFromProposalInitSubject.value;
  }

  resetAfterSuccessfulSave(): void {
    this.setSaveButtonDisabled(false);
  }

  /*******************************
   END MANAGE SAVE BUTTONS WHEN CREATING A CHANGE OF SCOPE
   *******************************/

  isOverDiscount(options: {
    discount: number | string | null | undefined;
    maxDiscount?: number | string | null;
  }): boolean {
    if (options.maxDiscount === null || options.maxDiscount === undefined || options.maxDiscount === '') {
      return false;
    }

    const discountValue = Number(options.discount) || 0;

    return discountValue > (Number(options.maxDiscount) || 0);
  }

}
